import React, { useContext, useId, useRef, useState, useLayoutEffect } from 'react';
import { Box, Button, darken, lighten, Tooltip } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { DateRange } from '@europrocurement/flexy-components/components/molecules/DateRangePicker';
import { useL2DTheme } from '@europrocurement/flexy-components';
import { FilterDatatable, FlexyFilterValue } from '../DatatableHeader';
import useFilter from './useFilter';
import { FlexyFilterContext } from './FlexyFilterContext';

export type FlexyFilterProps = {
    filter: FilterDatatable;
    value: FlexyFilterValue;
    filterLabel?: string;
    onChange: (
        filter: FilterDatatable,
        value: string | number | boolean | null | DateRange | string[],
        item?: unknown,
    ) => void;
    onClear: (filter: FilterDatatable) => void;
};

export const FlexyFilter: React.FunctionComponent<FlexyFilterProps> = function (props) {
    const { filter, value, filterLabel, onChange, onClear } = props;
    const { icon } = filter;
    const [hover, setHover] = useState<boolean>(false);

    const id = useId();
    const { getFilterLabel, getWrapperComponent, getWidgetComponent } = useFilter();
    const label = getFilterLabel(filter, value, filterLabel);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [expanded, setExpanded] = useState<boolean>(false);
    const { rawValue, setRawValue, setSelectedItem } = useContext(FlexyFilterContext);
    const widgetRef = useRef<HTMLButtonElement>(null);
    const { palette } = useL2DTheme();

    const buttonRef = useRef<HTMLButtonElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useLayoutEffect(() => {
        if (buttonRef.current) {
            setIsOverflowing(buttonRef.current.scrollWidth > buttonRef.current.clientWidth);
        }
    }, [label]);

    const wrapperFn = getWrapperComponent(filter);
    const widgetFn = getWidgetComponent(filter);

    const backgroundColor = React.useMemo(() => {
        if (hover || value !== null) {
            return palette.mode === 'light'
                ? lighten(palette.primary.light, 0.8)
                : darken(palette.primary.main, 0.4);
        }
        return palette.mode === 'light'
            ? lighten(palette.secondary.dark, 0.9)
            : darken(palette.secondary.light, 0.6);
    }, [hover, value, palette]);

    const handleFocus = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        setExpanded(true);

        const currentRef: { getValueOnFocus: unknown } = widgetRef.current;

        if (currentRef && currentRef.getValueOnFocus) {
            onChange(filter, currentRef.getValueOnFocus());
        }
    };
    const handleUnfocus = () => {
        setAnchorEl(null);
        setExpanded(false);
    };
    const handleClear = () => {
        setHover(false);
        onClear(filter);
    };
    const handleChangeValue = (v: string | boolean | number | null, item?: unknown) => {
        if (undefined !== v) {
            setRawValue(v);
        }
        if (item !== undefined) {
            setSelectedItem(item);
        }

        if (!filter.multiple && ['selectApi', 'staticSelect'].includes(filter.type)) {
            setAnchorEl(null);
            setExpanded(false);
            onChange(filter, v);
        }
    };

    const handleSubmit = () => {
        setAnchorEl(null);
        setExpanded(false);
        onChange(filter, rawValue);
    };

    let wrapper = null;
    let widget = null;

    if (widgetFn) {
        widget = React.createElement(
            widgetFn,
            {
                key: filter.field,
                value,
                filter,
                onChange: handleChangeValue,
                ref: widgetRef,
            },
            [],
        );
    }

    if (wrapperFn) {
        wrapper = React.createElement(
            wrapperFn,
            {
                id,
                anchorEl,
                open: expanded,
                onSubmit: handleSubmit,
                onCancel: handleUnfocus,
                value,
            },
            widget ? [widget] : [],
        );
    }

    return (
        <>
            <Box
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                className="flexy-filter"
                sx={{
                    padding: '4px 8px 4px 8px',
                    borderRadius: '12px',
                    display: 'flex',
                    gap: '0 4px',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    background: backgroundColor,
                }}
            >
                {isOverflowing ? (
                    <Tooltip
                        title={label}
                        enterDelay={500}
                        leaveDelay={200}
                    >
                        <Button
                            aria-describedby={id}
                            onClick={handleFocus}
                            ref={buttonRef}
                            sx={{
                                color: palette.getContrastText(backgroundColor),
                                display: 'flex',
                                gap: '0 4px',
                                alignItems: 'center',
                                justifyContent: 'start',
                                padding: '0',
                                minWidth: 'auto',
                                fontSize: '13px',
                                fontWeight: '500',
                                lineHeight: '20.8px',
                                textAlign: 'left',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '150px',
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    right: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: '20px',
                                    background: `linear-gradient(to right, transparent, ${backgroundColor})`,
                                    pointerEvents: 'none',
                                },
                            }}
                        >
                            {icon && (
                                <FaOptionIcon
                                    sx={{ fontSize: '12px', lineHeight: '0' }}
                                    icon={['fasl', icon]}
                                />
                            )}
                            {label}
                        </Button>
                    </Tooltip>
                ) : (
                    <Button
                        color="inherit"
                        aria-describedby={id}
                        onClick={handleFocus}
                        ref={buttonRef}
                        sx={{
                            display: 'flex',
                            gap: '0 4px',
                            alignItems: 'center',
                            justifyContent: 'start',
                            padding: '0',
                            minWidth: 'auto',
                            fontSize: '13px',
                            fontWeight: '500',
                            lineHeight: '20.8px',
                            textAlign: 'left',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '150px',
                        }}
                    >
                        {icon && (
                            <FaOptionIcon
                                sx={{ fontSize: '12px', lineHeight: '0' }}
                                icon={['fasl', icon]}
                            />
                        )}
                        {label}
                    </Button>
                )}
                {value !== null && (
                    <Button
                        sx={{
                            padding: '0',
                            fontSize: '12px',
                            fontWeight: '900',
                            lineHeight: '12px',
                            textAlign: 'center',
                            minWidth: 'auto',
                        }}
                        onClick={handleClear}
                    >
                        <FaOptionIcon
                            color={
                                palette.mode === 'light'
                                    ? lighten(palette.primary.main, 0.2)
                                    : palette.grey[400]
                            }
                            icon="times"
                            size="sm"
                        />
                    </Button>
                )}
            </Box>
            {wrapper}
        </>
    );
};
